import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  FormControl,
  FormGroup,
  FormLabel,
  Modal,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import styles from "./QueryModals.module.css";
import QueryDetailsSummary from "./QueryDetailsSummary";
import api from "../../../../utils/ApiMethod";
import { RootState } from "../../../../redux/store";
import { logoutUser } from "../../../../redux/actions";
import { ADMIN_LOGIN_BASE_URL } from "../../../../apiservices/apiService";

const QueryUpdateModal = ({
  data,
  onUpdated,
}: {
  data: any;
  onUpdated?: () => void;
}) => {
  const dispatch = useDispatch();
  const [show, setShow] = useState(false);
  const [textAreaForRemarks, setTextAreaForRemarks] = useState("");
  const [changeStatusOfQuery, setChangeStatusOfQuery] = useState(
    data?.status || ""
  );
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [emptyFieldMessage, setEmptyFieldMessage] = useState("");

  const { user } = useSelector((state: RootState) => ({
    user: state.Auth.user,
  }));

  const profileId = user?._id ? user._id : user?.user ? user.user._id : "";
  const Key_Key = user?.moniterd_ttl
    ? user.moniterd_ttl
    : user?.user
    ? user.user.moniterd_ttl
    : "";

  const handleShow = () => {
    setChangeStatusOfQuery(data?.status || "");
    setTextAreaForRemarks("");
    setEmptyFieldMessage("");
    setErrorMessage("");
    setShow(true);
  };
  const handleClose = () => setShow(false);

  const handleUpdateQueryStatus = async () => {
    if (!changeStatusOfQuery || !textAreaForRemarks.trim()) {
      setEmptyFieldMessage("Status and remarks are both required.");
      return;
    }
    try {
      setLoading(true);
      setEmptyFieldMessage("");
      const formData = new FormData();
      formData.append("updatedStatus", changeStatusOfQuery);
      formData.append("queryStatusBeingUpdatedBy", profileId);
      formData.append("remarks", textAreaForRemarks);
      const response = await api.updatePatchData(
        `/api/update/status/of/${data?._id}/${profileId}/${Key_Key}/query`,
        formData
      );
      if (response?.status === 440) {
        alert("Session Expired");
        dispatch(logoutUser());
        window.location.href = ADMIN_LOGIN_BASE_URL;
        return;
      }
      if (response.status !== 200) {
        setErrorMessage(response?.message || "Failed to update the query.");
      } else {
        setMessage(response?.data?.message || "Query updated successfully.");
        onUpdated?.();
        setTimeout(() => {
          handleClose();
        }, 900);
      }
    } catch (err) {
      setErrorMessage("Something went wrong while updating the query.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (message || errorMessage) {
      const timer = setTimeout(() => {
        setMessage("");
        setErrorMessage("");
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [message, errorMessage]);

  return (
    <>
      <Button
        variant="outline-primary"
        size="sm"
        onClick={handleShow}
        className={styles.iconBtn}
        title="Update query status"
      >
        <i className="uil uil-edit-alt"></i>
      </Button>

      <Modal show={show} onHide={handleClose} size="lg" centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title as="h6" className="mb-0">
            Update Query Status
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <QueryDetailsSummary data={data} />

          <div className={styles.divider}></div>

          {message && (
            <Alert variant="success" className="py-2">
              {message}
            </Alert>
          )}
          {errorMessage && (
            <Alert variant="danger" className="py-2">
              {errorMessage}
            </Alert>
          )}

          <FormGroup className="mb-2">
            <FormLabel className="small fw-semibold mb-1">
              New Status
            </FormLabel>
            <FormControl
              as="select"
              size="sm"
              value={changeStatusOfQuery}
              onChange={(e) => setChangeStatusOfQuery(e.target.value)}
            >
              <option value="">Select a Status</option>
              <option value="Open">Open</option>
              <option value="In-Review">In-Review</option>
              <option value="Pending">Pending</option>
              <option value="Closed">Closed</option>
            </FormControl>
          </FormGroup>

          <FormGroup>
            <FormLabel className="small fw-semibold mb-1">Remarks</FormLabel>
            <FormControl
              as="textarea"
              size="sm"
              value={textAreaForRemarks}
              onChange={(e) => setTextAreaForRemarks(e.target.value)}
              rows={3}
              placeholder="Add a note about this status change..."
            />
            {emptyFieldMessage && (
              <div className="text-danger small mt-1">
                {emptyFieldMessage}
              </div>
            )}
          </FormGroup>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleUpdateQueryStatus}
            disabled={loading}
          >
            {loading ? "Updating..." : "Update Status"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default QueryUpdateModal;
